/* eslint-disable indent */
import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import * as THREE from 'three';

import LottieScoreAnimation from '../components/LottieScoreAnimation';

import Header from '../components/header/Header';

import {
  loadFromLocalStorage,
  saveToLocalStorage,
  removeFromLocalStorage
} from '../utils/storage';

import './GameScreen.css';

const GameScreen = () => {
  // Camera pan flag for gameOver animation
  const cameraPanningRef = useRef(false);
  const [lottieScore, setLottieScore] = useState(null); // {x, y} or null
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mountRef = useRef(null);
  const totalGameTime = 59;

  useEffect(() => {
    try {
      saveToLocalStorage('gameSessionStarted', true);
    } catch (err) {
      // TODO: handle error
    }
  }, [searchParams]);

  const gameDataRef = useRef({
    score: 0,
    timeRemaining: totalGameTime,
    gameOver: false,
    cardsCollected: 0,
    giftsCollected: 0,
    consecutiveGiftsCount: 0,
    totalItemsCreated: 0,
    cardInPlay: false,
    lastGiftIndex: -1,
    lastCardIndex: -1,
    nextCardSequentialIndex: 0
  });

  const [_forceUpdate, setForceUpdate] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(false);
  const gameStartTimeRef = useRef(null);
  const gameReadyRef = useRef(false);
  const snowBgRef = useRef(null);

  const updateGameState = () => {
    setForceUpdate(prev => prev + 1);
  };

  const togglePause = useCallback(() => {
    setIsPaused(prev => {
      const newState = !prev;
      isPausedRef.current = newState;

      if (newState) {
        gameDataRef.current.pauseStartTime = Date.now();
      } else {
        if (gameDataRef.current.pauseStartTime && gameStartTimeRef.current) {
          const pauseDuration = Date.now() - gameDataRef.current.pauseStartTime;
          gameStartTimeRef.current += pauseDuration;
        }
        delete gameDataRef.current.pauseStartTime;
      }

      return newState;
    });
  }, []);

  const movingLeftRef = useRef(false);
  const movingRightRef = useRef(false);

  const handleLeftButtonDown = () => {
    movingLeftRef.current = true;
    movingRightRef.current = false;
  };

  const handleLeftButtonUp = () => {
    movingLeftRef.current = false;
  };

  const handleRightButtonDown = () => {
    movingRightRef.current = true;
    movingLeftRef.current = false;
  };

  const handleRightButtonUp = () => {
    movingRightRef.current = false;
  };

  const showFloatingScore = (points, x, y) => {
    if (points >= 1000) {
      setLottieScore({ x, y });
      setTimeout(() => setLottieScore(null), 1500);
      return;
    }
    const scoreElement = document.createElement('div');
    scoreElement.textContent = `+${points}`;
    scoreElement.className = 'floating-score';
    if (typeof x === 'number' && typeof y === 'number') {
      scoreElement.style.left = `${x}px`;
      scoreElement.style.top = `${y}px`;
      scoreElement.style.transform = 'translate(-50%, -50%)';
    }
    if (points > 100) {
      scoreElement.classList.add('text-gold');
    }
    scoreElement.style.animation = 'floatUp 2s ease-out forwards';
    if (mountRef && mountRef.current) {
      mountRef.current.appendChild(scoreElement);
    }
    setTimeout(() => {
      if (scoreElement.parentNode) {
        scoreElement.parentNode.removeChild(scoreElement);
      }
    }, 1500);
  };

  useEffect(() => {
    if (!mountRef.current) return;

    const currentMount = mountRef.current;

    const handleSelectStart = e => e.preventDefault();
    document.addEventListener('selectstart', handleSelectStart);

    const handleKeyDown = event => {
      switch (event.key) {
        case 'Escape':
        case ' ':
        case 'p':
        case 'P':
          event.preventDefault();
          togglePause();
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          if (!isPausedRef.current) {
            event.preventDefault();
            movingLeftRef.current = true;
            movingRightRef.current = false;
          }
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          if (!isPausedRef.current) {
            event.preventDefault();
            movingRightRef.current = true;
            movingLeftRef.current = false;
          }
          break;
        default:
          break;
      }
    };

    const handleKeyUp = event => {
      switch (event.key) {
        case 'ArrowLeft':
        case 'a':
        case 'A':
          event.preventDefault();
          movingLeftRef.current = false;
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          event.preventDefault();
          movingRightRef.current = false;
          break;
        default:
          break;
      }
    };

    const scene = new THREE.Scene();
    scene.background = null;

    const camera = new THREE.PerspectiveCamera(
      70,
      currentMount.clientWidth / currentMount.clientHeight,
      0.1,
      1000
    );

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance'
    });

    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = false;

    renderer.setSize(currentMount.clientWidth, currentMount.clientHeight);
    currentMount.appendChild(renderer.domElement);

    const gifts = [];
    const collectedGifts = [];
    const processedGifts = new Set();
    let giftIdCounter = 0;
    let player = null;

    const snowParticles = [];
    const snowParticleCount = 100;

    const towerGroup = new THREE.Group();
    scene.add(towerGroup);

    gameStartTimeRef.current = null;
    gameReadyRef.current = false;
    const currentGiftSpeed = 0.035;

    const fromParam = searchParams.get('from');
    if (fromParam === 'resume') {
      const data = loadFromLocalStorage('gameData');
      if (data && data.time) {
        const [minutes, seconds] = data.time.split(':').map(Number);
        const timeRemainingInSeconds = minutes * 59 + seconds;

        gameDataRef.current = {
          ...gameDataRef.current,
          score: data.score,
          timeRemaining: timeRemainingInSeconds,
          cardsCollected: data.cardsCollected || 0,
          giftsCollected: data.giftsCollected || 0,
          consecutiveGiftsCount: data.consecutiveGiftsCount || 0,
          totalItemsCreated: data.totalItemsCreated || 0,
          cardInPlay: data.cardInPlay || false,
          lastGiftIndex: data.lastGiftIndex || -1,
          lastCardIndex: data.lastCardIndex || -1,
          nextCardSequentialIndex: data.nextCardSequentialIndex || 0
        };

        const elapsedTime = totalGameTime - timeRemainingInSeconds;
        gameStartTimeRef.current = Date.now() - elapsedTime * 1000;
      }
    }

    camera.position.set(0, 0, 10);

    const frustumSize = 10;
    const aspect = currentMount.clientWidth / currentMount.clientHeight;
    const leftLimit = -(frustumSize * aspect) / 2 + 0.2;
    const rightLimit = (frustumSize * aspect) / 2 - 0.2;

    const textureLoader = new THREE.TextureLoader();
    textureLoader.load('/assets/game/snowman.png', texture => {
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.magFilter = THREE.LinearFilter;
      texture.minFilter = THREE.LinearMipmapLinearFilter;
      texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
      texture.generateMipmaps = true;

      const planeGeometry = new THREE.PlaneGeometry(3, 3);
      const planeMaterial = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        side: THREE.DoubleSide
      });
      player = new THREE.Mesh(planeGeometry, planeMaterial);
      player.position.y = -4;
      scene.add(player);
      if (snowBgRef.current) {
        snowBgRef.current.style.transform = `translateY(${player.position.y * 20}px)`;
      }
    });

    const giftTextures = [];
    const cardTexture = [];
    const giftTextureLoader = new THREE.TextureLoader();

    const giftImagePaths = [
      {
        path: '/assets/game/gift_1.png',
        color: 'red'
      },
      {
        path: '/assets/game/gift_2.png',
        color: 'green'
      },
      {
        path: '/assets/game/gift_3.png',
        color: 'yellow'
      }
    ];

    const cardImageData = [
      {
        path: '/assets/game/card_1.jpg',
        width: 1.2,
        height: 1.6
      },
      {
        path: '/assets/game/card_2.jpg',
        width: 1.2,
        height: 1.6
      },
      {
        path: '/assets/game/card_3.jpg',
        width: 1.2,
        height: 1.6
      },
      {
        path: '/assets/game/card_4.jpg',
        width: 1.2,
        height: 1.4
      },
      {
        path: '/assets/game/card_5.jpg',
        width: 1.2,
        height: 1 // Increased from 0.8 to make it more visible
      },
      {
        path: '/assets/game/card_6.jpg',
        width: 1.2,
        height: 1.2
      }
    ];

    giftImagePaths.forEach((giftData, index) => {
      giftTextureLoader.load(giftData.path, texture => {
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.magFilter = THREE.LinearFilter;
        texture.minFilter = THREE.LinearMipmapLinearFilter;
        texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
        texture.generateMipmaps = true;

        giftTextures[index] = texture;
      });
    });

    cardImageData.forEach((cardData, index) => {
      giftTextureLoader.load(cardData.path, texture => {
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.magFilter = THREE.LinearFilter;
        texture.minFilter = THREE.LinearMipmapLinearFilter;
        texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
        texture.generateMipmaps = true;

        cardTexture[index] = texture;
      });
    });

    createSnowParticles();

    function isPositionOverlapping(x, y, minDistance = 2.5) {
      for (const existingGift of gifts) {
        const distance = Math.sqrt(
          Math.pow(existingGift.position.x - x, 2) +
            Math.pow(existingGift.position.y - y, 2)
        );
        if (distance < minDistance) {
          return true;
        }
      }
      return false;
    }

    function createSnowParticles() {
      for (let i = 0; i < snowParticleCount; i++) {
        const geometry = new THREE.SphereGeometry(0.06, 10, 10);
        const material = new THREE.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.8
        });
        const snowflake = new THREE.Mesh(geometry, material);

        snowflake.position.x = (Math.random() - 0.5) * 20;
        snowflake.position.y = Math.random() * 15 + 5;
        snowflake.position.z = (Math.random() - 0.5) * 10;

        snowflake.userData = {
          fallSpeed: 0.01 + Math.random() * 0.02,
          swaySpeed: 0.005 + Math.random() * 0.01,
          swayRange: 0.5 + Math.random() * 1,
          originalX: snowflake.position.x,
          time: Math.random() * Math.PI * 2
        };

        scene.add(snowflake);
        snowParticles.push(snowflake);
      }
    }

    function updateSnowParticles() {
      snowParticles.forEach(snowflake => {
        snowflake.position.y -= snowflake.userData.fallSpeed;

        snowflake.userData.time += snowflake.userData.swaySpeed;
        snowflake.position.x =
          snowflake.userData.originalX +
          Math.sin(snowflake.userData.time) * snowflake.userData.swayRange;

        if (snowflake.position.y < camera.position.y - 8) {
          snowflake.position.y = camera.position.y + 10 + Math.random() * 5;
          snowflake.position.x = (Math.random() - 0.5) * 20;
          snowflake.userData.originalX = snowflake.position.x;
          snowflake.userData.time = Math.random() * Math.PI * 2;
        }
      });
    }

    function getNextGiftIndex(lastIndex) {
      if (giftImagePaths.length <= 1) {
        return 0;
      }

      const availableIndices = [];
      for (let idx = 0; idx < giftImagePaths.length; idx++) {
        if (idx !== lastIndex) {
          availableIndices.push(idx);
        }
      }

      if (availableIndices.length === 0) {
        return (lastIndex + 1) % giftImagePaths.length;
      }

      return availableIndices[
        Math.floor(Math.random() * availableIndices.length)
      ];
    }

    function createGift(forceCard = false) {
      const currentTime = Date.now();
      const elapsedTime = (currentTime - gameStartTimeRef.current) / 1000;

      const difficultyLevel = Math.floor(elapsedTime / 20);
      const maxGifts = Math.min(3 + difficultyLevel, 6);

      if (gifts.length < maxGifts) {
        const giftsToCreate = 1;
        let cardCreatedThisRound = false;

        for (let i = 0; i < giftsToCreate; i++) {
          let gift;

          const shouldCreateCardNow = forceCard && !cardCreatedThisRound;

          if (shouldCreateCardNow && cardTexture.length > 0) {
            cardCreatedThisRound = true;
            const cardIndex = gameDataRef.current.nextCardSequentialIndex;
            gameDataRef.current.nextCardSequentialIndex =
              (cardIndex + 1) % cardImageData.length;

            const selectedCardData = cardImageData[cardIndex];
            const selectedCardTexture = cardTexture[cardIndex];

            if (selectedCardTexture && selectedCardData) {
              const planeGeometry = new THREE.PlaneGeometry(
                selectedCardData.width,
                selectedCardData.height
              );
              const planeMaterial = new THREE.MeshBasicMaterial({
                map: selectedCardTexture,
                transparent: true,
                side: THREE.DoubleSide
              });
              gift = new THREE.Mesh(planeGeometry, planeMaterial);
              gift.userData = {
                id: ++giftIdCounter,
                collected: false,
                type: 'card',
                points: 1000,
                cardIndex: cardIndex
              };
            }
          } else if (giftTextures.length > 0) {
            const randomIndex = getNextGiftIndex(
              gameDataRef.current.lastGiftIndex
            );
            gameDataRef.current.lastGiftIndex = randomIndex;

            const selectedTexture = giftTextures[randomIndex];
            if (selectedTexture) {
              const planeGeometry = new THREE.PlaneGeometry(1.2, 1.2);
              const planeMaterial = new THREE.MeshBasicMaterial({
                map: selectedTexture,
                transparent: true,
                side: THREE.DoubleSide
              });
              gift = new THREE.Mesh(planeGeometry, planeMaterial);

              const randomScale = 0.8 + Math.random() * 0.4;
              gift.scale.set(randomScale, randomScale, randomScale);
              gift.userData = {
                id: ++giftIdCounter,
                collected: false,
                type: 'gift',
                giftType: giftImagePaths[randomIndex].color,
                giftIndex: randomIndex,
                points: 100,
                createdAfterIndex: gameDataRef.current.lastGiftIndex
              };
            }
          }

          let randomX, randomY;
          let attempts = 0;
          const maxAttempts = 10;

          do {
            randomX = leftLimit + Math.random() * (rightLimit - leftLimit);
            const randomYOffset = Math.random() * 2;

            const topOfScreen = camera.position.y + 6;
            randomY = topOfScreen + randomYOffset;
            attempts++;
          } while (
            isPositionOverlapping(randomX, randomY) &&
            attempts < maxAttempts
          );

          if (
            attempts >= maxAttempts &&
            isPositionOverlapping(randomX, randomY)
          ) {
            if (shouldCreateCardNow) {
              gameDataRef.current.cardInPlay = false;
              cardCreatedThisRound = false;
            }
            continue;
          }

          if (gift) {
            gift.position.set(randomX, randomY, 0);
            gift.rotation.set(0, 0, 0);
            scene.add(gift);
            gifts.push(gift);

            if (
              !gameReadyRef.current &&
              gameDataRef.current.totalItemsCreated === 0
            ) {
              gameStartTimeRef.current = Date.now();
              gameReadyRef.current = true;
            }

            gameDataRef.current.totalItemsCreated++;

            if (gift.userData && gift.userData.type === 'card') {
              gameDataRef.current.cardInPlay = true;
            }
          } else if (shouldCreateCardNow) {
            gameDataRef.current.cardInPlay = false;
            cardCreatedThisRound = false;
          }
        }
      }
    }

    function showGameOver() {
      gifts.forEach(gift => {
        if (!gift.userData?.isStacked && scene.children.includes(gift)) {
          scene.remove(gift);
        }
      });
      cameraPanningRef.current = true;
      let totalStackHeight = 1.2;
      for (let j = 0; j < collectedGifts.length; j++) {
        const stackedGift = collectedGifts[j];
        const scaleY = stackedGift.scale ? stackedGift.scale.y : 1;
        let baseHeight;
        if (stackedGift.userData && stackedGift.userData.type === 'card') {
          if (stackedGift.userData.cardIndex !== undefined) {
            baseHeight = cardImageData[stackedGift.userData.cardIndex].height;
          } else {
            baseHeight = 1.4;
          }
        } else {
          baseHeight = 1.2;
        }
        // eslint-disable-next-line no-unused-vars
        totalStackHeight += baseHeight * scaleY;
      }

      const frustumHeight = 10;
      const targetCameraY = player.position.y + frustumHeight / 2;
      const startY = camera.position.y;
      const snowBgStartY = snowBgRef.current
        ? parseFloat(
            snowBgRef.current.style.transform?.replace(/[^-?\d.]/g, '') || '0'
          )
        : 0;
      const snowBgTargetY = targetCameraY * 20;
      const distance = Math.abs(targetCameraY - startY);
      const animationDuration = Math.max(700, Math.min(1700, distance * 80));
      let animationDone = false;
      const startTime = performance.now();

      function animateCameraDown(now) {
        const elapsed = now - startTime;
        let t = Math.min(elapsed / animationDuration, 1);
        t = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        camera.position.y = startY + (targetCameraY - startY) * t;
        if (snowBgRef.current) {
          const snowY = snowBgStartY + (snowBgTargetY - snowBgStartY) * t;
          snowBgRef.current.style.transform = `translateY(${snowY}px)`;
        }
        renderer.render(scene, camera);
        if (t < 1) {
          requestAnimationFrame(animateCameraDown);
        } else if (!animationDone) {
          animationDone = true;
          cameraPanningRef.current = false;
          setTimeout(finishGameOver, 1000);
        }
      }

      function finishGameOver() {
        if (gameDataRef.current.gameOver) return;
        gameDataRef.current.gameOver = true;
        collectedGifts.forEach(gift => {
          scene.remove(gift);
        });
        collectedGifts.length = 0;
        const finalData = {
          score: gameDataRef.current.score,
          cardsCollected: gameDataRef.current.cardsCollected,
          giftsCollected: gameDataRef.current.giftsCollected,
          timestamp: Date.now()
        };
        saveToLocalStorage('gameResults', finalData);
        removeFromLocalStorage('gameData');
        navigate(
          '/nice-job?score=' +
            gameDataRef.current.score +
            '&cards=' +
            gameDataRef.current.cardsCollected
        );
      }

      requestAnimationFrame(animateCameraDown);
    }

    function animate() {
      try {
        if (gameDataRef.current.gameOver && !cameraPanningRef.current) return;

        requestAnimationFrame(animate);

        if (isPausedRef.current) {
          renderer.render(scene, camera);
          return;
        }
        for (let j = 0; j < collectedGifts.length; j++) {
          const stackedGift = collectedGifts[j];
          if (stackedGift.userData && stackedGift.userData.isAnimating) {
            stackedGift.rotation.z +=
              stackedGift.userData.rotationSpeed *
              stackedGift.userData.rotationDirection;
          }
        }

        if (gameStartTimeRef.current) {
          const currentTime = Date.now();
          const elapsedTime = (currentTime - gameStartTimeRef.current) / 1000;
          const newTimeRemaining = Math.max(0, 59 - elapsedTime);

          if (
            Math.floor(newTimeRemaining) !==
            Math.floor(gameDataRef.current.timeRemaining)
          ) {
            gameDataRef.current.timeRemaining = newTimeRemaining;
            updateGameState();
          } else {
            gameDataRef.current.timeRemaining = newTimeRemaining;
          }

          if (gameDataRef.current.timeRemaining <= 1) {
            showGameOver();
            return;
          }
        } else {
          gameDataRef.current.timeRemaining = 59;
        }

        let expectedItems = 0;
        const itemInterval = 400;
        if (gameStartTimeRef.current) {
          const gameTime = Date.now();
          const timeSinceStart = gameTime - gameStartTimeRef.current;
          if (timeSinceStart >= 0) {
            expectedItems = Math.floor(timeSinceStart / itemInterval) + 1;
          }
        } else {
          if (gameDataRef.current.totalItemsCreated === 0) {
            expectedItems = 1;
          }
        }
        if (
          gameDataRef.current.timeRemaining > 0.5 &&
          gameDataRef.current.totalItemsCreated < expectedItems
        ) {
          const nextItemPosition =
            (gameDataRef.current.totalItemsCreated % 4) + 1;
          const shouldForceCard = nextItemPosition === 4;
          createGift(shouldForceCard);
        }

        updateSnowParticles();

        for (let i = gifts.length - 1; i >= 0; i--) {
          const gift = gifts[i];

          if (!gift || !gift.position) {
            gifts.splice(i, 1);
            continue;
          }

          gift.position.y -= currentGiftSpeed;

          for (let j = 0; j < gifts.length; j++) {
            if (i !== j) {
              const otherGift = gifts[j];

              if (!otherGift || !otherGift.position) {
                continue;
              }

              const distance = Math.sqrt(
                Math.pow(gift.position.x - otherGift.position.x, 2) +
                  Math.pow(gift.position.y - otherGift.position.y, 2)
              );

              if (distance < 1.5 && distance > 0) {
                const pushForce = 0.03;
                const direction =
                  gift.position.x > otherGift.position.x ? 1 : -1;

                gift.position.x = Math.max(
                  leftLimit,
                  Math.min(rightLimit, gift.position.x + direction * pushForce)
                );
                otherGift.position.x = Math.max(
                  leftLimit,
                  Math.min(
                    rightLimit,
                    otherGift.position.x - direction * pushForce
                  )
                );
              }
            }
          }

          let collisionTargetX, collisionTargetY;
          let shouldCheckCollision = false;

          if (collectedGifts.length > 0) {
            const topGift = collectedGifts[collectedGifts.length - 1];

            const worldPosition = new THREE.Vector3();
            topGift.getWorldPosition(worldPosition);

            collisionTargetX = worldPosition.x;

            let topElementHeight;
            const scaleY = topGift.scale ? topGift.scale.y : 1;

            if (topGift.userData && topGift.userData.type === 'card') {
              if (topGift.userData.cardIndex !== undefined) {
                topElementHeight =
                  cardImageData[topGift.userData.cardIndex].height;
              } else {
                topElementHeight = 1.4;
              }
            } else {
              topElementHeight = 1.2;
            }

            collisionTargetY =
              worldPosition.y + topElementHeight * scaleY * 0.5;
            shouldCheckCollision = true;
          } else if (player && player.position) {
            collisionTargetX = player.position.x;
            collisionTargetY = player.position.y + 1.2;
            shouldCheckCollision = true;
          }

          if (!shouldCheckCollision) {
            continue;
          }

          const distance = Math.sqrt(
            Math.pow(gift.position.x - collisionTargetX, 2) +
              Math.pow(gift.position.y - collisionTargetY, 2)
          );

          if (
            gift.userData &&
            !gift.userData.collected &&
            !processedGifts.has(gift.userData.id) &&
            distance < 0.6 &&
            gift.position.y > collisionTargetY - 0.9 &&
            gift.position.y < collisionTargetY + 0.9
          ) {
            processedGifts.add(gift.userData.id);

            gift.userData.collected = true;

            gifts.splice(i, 1);
            i--;

            const points = gift.userData.points || 100;
            gameDataRef.current.score += points;

            const vector = new THREE.Vector3(
              gift.position.x,
              gift.position.y,
              gift.position.z
            );
            vector.project(camera);

            const x = (vector.x * 0.5 + 0.5) * currentMount.clientWidth;
            const y = (vector.y * -0.5 + 0.5) * currentMount.clientHeight;

            showFloatingScore(points, x, y);

            if (gift.userData.type === 'card') {
              gameDataRef.current.cardsCollected++;
              gameDataRef.current.cardInPlay = false;
            } else {
              gameDataRef.current.giftsCollected++;
            }

            let stackY = 1.2;

            for (let j = 0; j < collectedGifts.length; j++) {
              const stackedGift = collectedGifts[j];
              const scaleY = stackedGift.scale ? stackedGift.scale.y : 1;

              let baseHeight;
              if (
                stackedGift.userData &&
                stackedGift.userData.type === 'card'
              ) {
                if (stackedGift.userData.cardIndex !== undefined) {
                  baseHeight =
                    cardImageData[stackedGift.userData.cardIndex].height;
                } else {
                  baseHeight = 1.4;
                }
              } else {
                baseHeight = 1.2;
              }

              stackY += baseHeight * scaleY;
            }

            const newElementScaleY = gift.scale ? gift.scale.y : 1;
            let newElementBaseHeight;
            if (gift.userData && gift.userData.type === 'card') {
              if (gift.userData.cardIndex !== undefined) {
                newElementBaseHeight =
                  cardImageData[gift.userData.cardIndex].height;
              } else {
                newElementBaseHeight = 1.4;
              }
            } else {
              newElementBaseHeight = 1.2;
            }
            stackY += newElementBaseHeight * newElementScaleY * 0.5;

            scene.remove(gift);

            gift.position.set(0.25, stackY, 0);
            gift.rotation.set(0, 0, 0);
            gift.userData.isStacked = true;
            gift.userData.stackIndex = collectedGifts.length;
            gift.userData.isAnimating = false;

            towerGroup.add(gift);
            collectedGifts.push(gift);

            if (player && player.position) {
              towerGroup.position.x = player.position.x;
              towerGroup.position.y = player.position.y;
            }

            updateGameState();
            continue;
          }
          let collisionThresholdY;
          if (collectedGifts.length > 0) {
            const topGift = collectedGifts[collectedGifts.length - 1];
            const scaleY = topGift.scale ? topGift.scale.y : 1;
            let topElementHeight;
            if (topGift.userData && topGift.userData.type === 'card') {
              if (topGift.userData.cardIndex !== undefined) {
                topElementHeight =
                  cardImageData[topGift.userData.cardIndex].height;
              } else {
                topElementHeight = 1.4;
              }
            } else {
              topElementHeight = 1.2;
            }
            const worldPosition = new THREE.Vector3();
            topGift.getWorldPosition(worldPosition);
            collisionThresholdY =
              worldPosition.y + topElementHeight * scaleY * 0.5;
          } else if (player && player.position) {
            collisionThresholdY = player.position.y + 1.2;
          } else {
            collisionThresholdY = 0;
          }

          if (
            !gift.userData.isAnimating &&
            !gift.userData.collected &&
            distance >= 0.6 &&
            distance < 2 &&
            gift.position.y < collisionThresholdY
          ) {
            gift.userData.isAnimating = true;
            gift.userData.fallStartTime = Date.now();
            gift.userData.initialRotation = gift.rotation.z || 0;
            gift.userData.rotationDirection = Math.random() - 0.5 > 0 ? 1 : -1;
            gift.userData.rotationSpeed = 0.05 + Math.random() * 0.05;
          }

          if (gift.userData.isAnimating) {
            const animationDuration = 3000;
            const elapsed = Date.now() - gift.userData.fallStartTime;
            const progress = Math.min(elapsed / animationDuration, 1);

            gift.rotation.z +=
              gift.userData.rotationSpeed * gift.userData.rotationDirection;

            gift.position.x += (Math.random() - 0.5) * 0.02;

            if (gift.material) {
              gift.material.opacity = Math.max(0.2, 1 - progress * 0.8);
              gift.material.transparent = true;
            }
          }

          if (gift.position.y < -8) {
            if (gift.userData && gift.userData.type === 'card') {
              gameDataRef.current.cardInPlay = false;
            }
            scene.remove(gift);
            gifts.splice(i, 1);
          }
        }

        if (player) {
          const playerSpeed = 0.15;

          if (movingLeftRef.current) {
            player.position.x = Math.max(
              leftLimit,
              player.position.x - playerSpeed
            );
          }
          if (movingRightRef.current) {
            player.position.x = Math.min(
              rightLimit,
              player.position.x + playerSpeed
            );
          }

          const isMoving = movingLeftRef.current || movingRightRef.current;

          if (player && player.position) {
            towerGroup.position.x = player.position.x;
            towerGroup.position.y = player.position.y;
          }

          if (isMoving && player && player.position) {
            const direction = movingLeftRef.current ? -1 : 1;
            const maxTilt = 0.1;

            const stackHeight = collectedGifts.length;
            let lerpSpeed = 0.15;
            let straightenSpeed = 0.25;
            if (stackHeight > 4) {
              lerpSpeed = 0.09;
              straightenSpeed = 0.13;
            }
            if (stackHeight > 8) {
              lerpSpeed = 0.05;
              straightenSpeed = 0.08;
            }

            const edgeThreshold = 0.3;
            const distanceFromLeftEdge = Math.abs(
              player.position.x - leftLimit
            );
            const distanceFromRightEdge = Math.abs(
              player.position.x - rightLimit
            );
            const isNearEdge =
              distanceFromLeftEdge < edgeThreshold ||
              distanceFromRightEdge < edgeThreshold;

            if (isNearEdge) {
              towerGroup.rotation.z +=
                (0 - towerGroup.rotation.z) * straightenSpeed;
            } else {
              const targetRotation = direction * maxTilt;
              towerGroup.rotation.z +=
                (targetRotation - towerGroup.rotation.z) * lerpSpeed;
            }
          } else {
            const stackHeight = collectedGifts.length;
            let returnFactor = 0.88;
            if (stackHeight > 4) returnFactor = 0.94;
            if (stackHeight > 8) returnFactor = 0.97;
            towerGroup.rotation.z *= returnFactor;
          }
        }

        if (player && collectedGifts.length > 0) {
          let totalStackHeight = 1.2;
          for (let j = 0; j < collectedGifts.length; j++) {
            const stackedGift = collectedGifts[j];
            const scaleY = stackedGift.scale ? stackedGift.scale.y : 1;

            let baseHeight;
            if (stackedGift.userData && stackedGift.userData.type === 'card') {
              if (stackedGift.userData.cardIndex !== undefined) {
                baseHeight =
                  cardImageData[stackedGift.userData.cardIndex].height;
              } else {
                baseHeight = 1.4;
              }
            } else {
              baseHeight = 1.2;
            }
            totalStackHeight += baseHeight * scaleY;
          }

          const frustumHeight = 10;
          const screenPercent = 0.5;
          const targetWorldY =
            frustumHeight * screenPercent - frustumHeight / 2;
          const topGiftY = player.position.y + totalStackHeight;
          let targetCameraY = 0;
          if (topGiftY > targetWorldY) {
            targetCameraY = topGiftY - targetWorldY;
            const baseY = player.position.y;
            if (targetCameraY < baseY) targetCameraY = baseY;
          } else {
            targetCameraY = 0;
          }
          const lerpFactor = 0.08;
          camera.position.y += (targetCameraY - camera.position.y) * lerpFactor;
          if (snowBgRef.current) {
            snowBgRef.current.style.transform = `translateY(${camera.position.y * 20}px)`;
          }
        } else if (collectedGifts.length === 0) {
          const lerpFactor = 0.08;
          camera.position.y += (0 - camera.position.y) * lerpFactor;

          if (snowBgRef.current) {
            snowBgRef.current.style.transform = `translateY(${camera.position.y * 20}px)`;
          }
        }

        renderer.render(scene, camera);
      } catch (err) {
        // TODO: handle error
      }
    }

    animate();

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      document.removeEventListener('selectstart', handleSelectStart);

      scene.remove(towerGroup);

      collectedGifts.forEach(gift => {
        if (gift.parent) {
          gift.parent.remove(gift);
        }
      });

      snowParticles.forEach(snowflake => {
        scene.remove(snowflake);
        if (snowflake.geometry) snowflake.geometry.dispose();
        if (snowflake.material) snowflake.material.dispose();
      });
      snowParticles.length = 0;

      if (currentMount && renderer.domElement) {
        currentMount.removeChild(renderer.domElement);
      }

      renderer.dispose();
    };
  }, [searchParams, navigate, togglePause]);

  const formatTime = timeInSeconds => {
    const seconds = Math.max(0, Math.floor(timeInSeconds));
    return `${seconds}`;
  };

  return (
    <div
      className='game-play-container'
      style={{
        height: isPaused ? '100%' : '100dvh',
        width: isPaused ? '100%' : '100dvw',
        maxWidth: isPaused ? '100%' : '600px',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none',
        WebkitTapHighlightColor: 'transparent'
      }}
    >
      <div className='background-game' />
      <Header
        score={gameDataRef.current.score}
        time={formatTime(gameDataRef.current.timeRemaining)}
        onPause={togglePause}
        isPaused={isPaused}
      />
      <div
        ref={mountRef}
        className='game-play-content'
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          outline: 'none',
          display: isPaused ? 'none' : 'block',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          WebkitTouchCallout: 'none',
          WebkitTapHighlightColor: 'transparent'
        }}
        onContextMenu={e => e.preventDefault()}
      >
        {lottieScore && (
          <div
            style={{
              position: 'absolute',
              left: typeof lottieScore.x === 'number' ? lottieScore.x : '50%',
              top: typeof lottieScore.y === 'number' ? lottieScore.y : '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              height: 400,
              pointerEvents: 'none',
              zIndex: 20
            }}
          >
            <LottieScoreAnimation style={{ width: '100%', height: '100%' }} />
            <div
              className='floating-score gold-1000'
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                fontSize: '26px',
                transform: 'translate(-50%, -50%)'
              }}
            >
              +1000
            </div>
          </div>
        )}
        <div
          className='background-game-stars'
          style={{
            backgroundImage: 'url(/assets/game/game-bg.png)'
          }}
        />
        <img
          ref={snowBgRef}
          src='/assets/game/game-snow.png'
          className='game-snow-bg'
          alt='Ground'
          width='100%'
        />
        <button
          id='game-left-arrow'
          className='game-left-arrow'
          aria-label='Move Left'
          type='button'
          onMouseDown={handleLeftButtonDown}
          onMouseUp={handleLeftButtonUp}
          onMouseLeave={handleLeftButtonUp}
          onTouchStart={() => {
            handleLeftButtonDown();
          }}
          onTouchEnd={() => {
            handleLeftButtonUp();
          }}
          onContextMenu={e => e.preventDefault()}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none',
            WebkitTapHighlightColor: 'transparent'
          }}
        >
          <img src='/assets/game/game-arrow.png' alt='Move Left' width='40' />
        </button>
        <button
          id='game-right-arrow'
          className='game-right-arrow'
          aria-label='Move Right'
          type='button'
          onMouseDown={handleRightButtonDown}
          onMouseUp={handleRightButtonUp}
          onMouseLeave={handleRightButtonUp}
          onTouchStart={() => {
            handleRightButtonDown();
          }}
          onTouchEnd={() => {
            handleRightButtonUp();
          }}
          onContextMenu={e => e.preventDefault()}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none',
            WebkitTapHighlightColor: 'transparent'
          }}
        >
          <img src='/assets/game/game-arrow.png' alt='Move Right' width='40' />
        </button>
      </div>
    </div>
  );
};

export default GameScreen;
